"use client"

import React, { createContext, useCallback, useContext, useRef } from "react"
import { Toaster, toaster } from "@/components/ui/toaster"

export type MessageMode =
  | "regular"
  | "success"
  | "error"
  | "scoreUpgrade"
  | "xpUpgrade"
  | "achievementUnlocked"
  | "levelUpgrade"

export type MessagePayload = {
  mode: MessageMode
  title?: string
  description?: string
  durationMs?: number
  closable?: boolean
  previousValue?: number
  currentValue?: number
  action?: {
    label: string
    onClick: VoidFunction
  }
}

type MessageContextType = {
  notify: (payload: MessagePayload) => void
  error: (description: string, title?: string) => void
}

const MessageContext = createContext<MessageContextType | undefined>(undefined)

const MODE_DEFAULTS: Record<
  MessageMode,
  { type: "info" | "success" | "error"; duration: number; title: string }
> = {
  regular: { type: "info", duration: 3200, title: "Notice" },
  success: { type: "success", duration: 3600, title: "Success" },
  error: { type: "error", duration: 4800, title: "Something went wrong!" },
  scoreUpgrade: { type: "success", duration: 6200, title: "Score Upgraded" },
  xpUpgrade: { type: "success", duration: 6500, title: "XP Increased" },
  achievementUnlocked: {
    type: "success",
    duration: 7200,
    title: "Achievement Unlocked",
  },
  levelUpgrade: { type: "success", duration: 7000, title: "Level Up" },
}

export const useMessage = () => {
  const ctx = useContext(MessageContext)
  if (!ctx) throw new Error("useMessage must be used within MessageProvider")
  return ctx
}

export const MessageProvider = ({ children }: { children: React.ReactNode }) => {
  const lastToastRef = useRef<{ signature: string; timestamp: number } | null>(null)

  const notify = useCallback((payload: MessagePayload) => {
    const defaults = MODE_DEFAULTS[payload.mode]
    const title = payload.title ?? defaults.title
    const signature = JSON.stringify({
      mode: payload.mode,
      title,
      description: payload.description ?? "",
      previousValue: payload.previousValue ?? null,
      currentValue: payload.currentValue ?? null,
    })
    const now = Date.now()
    const lastToast = lastToastRef.current

    if (
      lastToast &&
      lastToast.signature === signature &&
      now - lastToast.timestamp < 1200
    ) {
      return
    }

    lastToastRef.current = { signature, timestamp: now }

    const createToast = () =>
      toaster.create({
        type: defaults.type,
        title,
        description: payload.description,
        duration: payload.durationMs ?? defaults.duration,
        closable: payload.closable ?? true,
        action: payload.action,
        meta: {
          mode: payload.mode,
          previousValue: payload.previousValue,
          currentValue: payload.currentValue,
        },
      })

    queueMicrotask(createToast)
  }, [])

  const error = useCallback(
    (description: string, title?: string) => {
      notify({
        mode: "error",
        title,
        description,
      })
    },
    [notify],
  )

  return (
    <MessageContext.Provider value={{ notify, error }}>
      {children}
      <Toaster />
    </MessageContext.Provider>
  )
}
